// ROOM 404: Student Missions & Challenges Interface

import React, { useEffect, useState } from 'react';
import {
  Award,
  CheckCircle,
  Clock,
  Sparkles,
  Target,
  Upload,
  Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../services/api';
import type { Mission } from '../types';

export const Missions: React.FC = () => {
  const { refreshSession } = useAuth();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [selectedMission, setSelectedMission] = useState<Mission | null>(null);
  const [submissionText, setSubmissionText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchMissions = async () => {
    try {
      const data = await apiRequest<{ missions: Mission[] }>('/api/missions');
      setMissions(data.missions);
    } catch {
      // Ignored
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMissions();
  }, []);

  const handleJoin = async (missionId: string) => {
    try {
      await apiRequest(`/api/missions/${missionId}/join`, { method: 'POST' });
      fetchMissions();
    } catch (err: any) {
      alert(err.message || 'Failed to join mission');
    }
  };

  const handleSubmitProof = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMission || !submissionText.trim()) return;

    setSubmitting(true);
    try {
      await apiRequest(`/api/missions/${selectedMission.id}/submit`, {
        method: 'POST',
        body: { submissionText: submissionText.trim() },
      });

      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setSelectedMission(null);
        setSubmissionText('');
        fetchMissions();
        refreshSession();
      }, 2000);
    } catch (err: any) {
      alert(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 pb-24 max-w-md mx-auto px-4 pt-3">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-white tracking-tight">Society Missions</h2>
        <p className="text-xs text-gray-400">Campus challenges, exploration & reputation badges</p>
      </div>

      {/* Missions List */}
      <div className="space-y-3">
        {missions.map((mission) => {
          const isSubmitted = mission.user_status === 'submitted';
          const isAwarded = mission.user_status === 'awarded';
          const isJoined = mission.user_status === 'joined' || isSubmitted || isAwarded;

          return (
            <div
              key={mission.id}
              className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 uppercase">
                  {mission.category}
                </span>

                <div className="flex items-center space-x-1.5 text-xs text-amber-400 font-mono font-bold">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>+{mission.points} pts</span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-white tracking-tight">{mission.title}</h3>
                <p className="text-xs text-gray-300 mt-1 leading-relaxed">{mission.brief}</p>
              </div>

              <div className="p-2.5 rounded-xl bg-[#090a0f] border border-[#1f2433] text-[11px] text-gray-400 space-y-1">
                <div className="font-semibold text-gray-300">Requirements:</div>
                <div className="font-mono text-gray-400">{mission.submission_requirements}</div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-[#1f2433]/70">
                <span className="flex items-center space-x-1 text-xs text-gray-400 font-mono">
                  <Users className="w-3.5 h-3.5 text-gray-500" />
                  <span>{mission.participant_count || 0} participants</span>
                </span>

                {isAwarded ? (
                  <span className="px-3 py-1 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-semibold flex items-center space-x-1">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>Completed</span>
                  </span>
                ) : isSubmitted ? (
                  <span className="px-3 py-1 rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-semibold flex items-center space-x-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Pending Review</span>
                  </span>
                ) : isJoined ? (
                  <button
                    onClick={() => setSelectedMission(mission)}
                    className="px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold transition-colors"
                  >
                    Submit Proof
                  </button>
                ) : (
                  <button
                    onClick={() => handleJoin(mission.id)}
                    className="px-3 py-1.5 rounded-xl bg-[#1f2433] hover:bg-gray-700 text-white text-xs font-medium transition-colors"
                  >
                    Join Mission
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Submit Proof Modal */}
      {selectedMission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-[#12141c] border border-[#1f2433] rounded-2xl p-5 shadow-2xl">
            {submitSuccess ? (
              <div className="py-6 text-center space-y-3">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto" />
                <h3 className="text-base font-semibold text-white">Proof Submitted</h3>
                <p className="text-xs text-gray-400">
                  Mission proof recorded. Your points will be credited upon staff verification.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmitProof} className="space-y-4">
                <div>
                  <h3 className="text-base font-bold text-white mb-0.5">{selectedMission.title}</h3>
                  <p className="text-xs text-gray-400">{selectedMission.submission_requirements}</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    Your Submission / Proof Notes
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={submissionText}
                    onChange={(e) => setSubmissionText(e.target.value)}
                    placeholder="Describe how you fulfilled this mission challenge..."
                    className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-xs text-gray-200 focus:outline-none focus:border-emerald-500 resize-none"
                  />
                </div>

                <div className="flex items-center space-x-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setSelectedMission(null)}
                    className="flex-1 py-2 px-3 bg-[#1f2433] hover:bg-gray-700 text-gray-300 rounded-xl text-xs font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-2 px-3 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-xl text-xs disabled:opacity-50"
                  >
                    {submitting ? 'Submitting...' : 'Submit Proof'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
